/**
 * 录音前的环境检测 (docs/ROADMAP.md A7, §9 SNR ≥45 dB).
 *
 * At the start of a recording session the student records 2 s of room noise.
 * The RMS of that probe is the noise floor; every later take reports
 * `snr_db = 20·log10(speech RMS / noise floor)` in `client_meta`, so QC can
 * see which recordings were made in a bad room.
 */

export const NOISE_PROBE_MS = 2000;

const KEY = "xz.noise_check";

export type NoiseGrade = "quiet" | "ok" | "loud";

export interface NoiseCheck {
  rms: number;
  /** ISO timestamp of the probe. */
  at: string;
}

export interface NoiseGradePresentation {
  grade: NoiseGrade;
  label: string;
  advice: string;
}

/** Thresholds in RMS (≈ −48 dBFS and −34 dBFS). */
const QUIET_MAX = 0.004;
const OK_MAX = 0.02;

export function gradeNoise(rms: number): NoiseGradePresentation {
  if (rms < QUIET_MAX) {
    return {
      grade: "quiet",
      label: "安静",
      advice: "环境很好，可以开始录音了。",
    };
  }
  if (rms < OK_MAX) {
    return {
      grade: "ok",
      label: "一般",
      advice: "还能用。如果旁边有风扇、空调或说话声，关掉或换个位置会更好。",
    };
  }
  return {
    grade: "loud",
    label: "太吵",
    advice: "这里噪声偏大，录出来会听不清。换到安静的房间、关掉风扇和音乐，再测一次。",
  };
}

/** dB SNR of one take against the session's noise floor. */
export function snrDb(speechRms: number, noiseFloorRms: number): number | null {
  if (!(speechRms > 0) || !(noiseFloorRms > 0)) return null;
  return Number((20 * Math.log10(speechRms / noiseFloorRms)).toFixed(1));
}

/** The probe for this browsing session, or null when it has not run yet. */
export function readNoiseCheck(): NoiseCheck | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<NoiseCheck>;
    return typeof parsed.rms === "number" ? { rms: parsed.rms, at: parsed.at ?? "" } : null;
  } catch {
    return null;
  }
}

export function isNoiseChecked(): boolean {
  return readNoiseCheck() !== null;
}

export function saveNoiseCheck(rms: number): NoiseCheck {
  const check: NoiseCheck = { rms, at: new Date().toISOString() };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(check));
  } catch {
    /* private mode — the gate simply re-runs next navigation */
  }
  return check;
}

/** Test helper / 「重新检测」. */
export function clearNoiseCheck(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
